import { DOCUMENT } from '@angular/common';
import { Service, computed, effect, inject, signal } from '@angular/core';

import { SesionStore } from '@core/sesion/sesion.store';
import { FONDO_CLARO, FONDO_MENU, FONDO_OSCURO, colorClienteLegible } from './color-cliente';

export type Apariencia = 'claro' | 'oscuro' | 'sistema';
export type Densidad = 'comoda' | 'compacta';

const CLAVE_APARIENCIA = 'faro.apariencia';
const CLAVE_DENSIDAD = 'faro.densidad';

function leerLocal(clave: string): string | null {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribirLocal(clave: string, valor: string): void {
  try {
    localStorage.setItem(clave, valor);
  } catch {
    /* almacenamiento bloqueado: la preferencia dura lo que la pestaña */
  }
}

/**
 * Apariencia de la aplicación: claro/oscuro, densidad y tema por cliente.
 *
 * El modo oscuro se resuelve con `color-scheme` + `light-dark()` de Material 22,
 * así que alcanza con poner la clase `.dark-theme` en el `<body>`: no hay que
 * recalcular ninguna paleta.
 *
 * **FARO no toma el tema del cliente** (a diferencia de IRIX): el manual de
 * marca manda y la base es cyan, ink y neutros. El color primario del cliente
 * entra como **segundo color de identidad**, intercalado con el cyan en piezas
 * de identidad y navegación —nunca en botones de acción, enlaces, estados,
 * colores semánticos ni gráficos—. El logo va en el pie del menú y en los PDF.
 *
 * - `--color-cliente` (en el `<body>`): corrido lo justo para llegar a 3:1
 *   contra la superficie del modo actual.
 * - `--color-cliente-menu`: el mismo, contra el menú lateral, que es oscuro en
 *   los dos temas.
 *
 * Sin color, quien lo usa escribe `var(--color-cliente, <el cyan que iba>)`.
 * Dónde se usa: segmentos de tarjetas, zonas pares de los tableros, títulos de
 * listado, ícono de los diálogos, solapa activa, íconos de las tarjetas pares
 * de los detalles, ítem activo del menú, cliente activo en la cabecera y la
 * portada de los PDF. Buscar `--color-cliente` para la lista exacta.
 *
 * Los seis colores de estado de luminaria no los toca nadie: el estado significa
 * lo mismo en todos los municipios.
 */
@Service()
export class TemaService {
  private readonly documento = inject(DOCUMENT);
  private readonly sesion = inject(SesionStore);

  private readonly _apariencia = signal<Apariencia>(
    (leerLocal(CLAVE_APARIENCIA) as Apariencia | null) ?? 'sistema',
  );
  private readonly _densidad = signal<Densidad>(
    (leerLocal(CLAVE_DENSIDAD) as Densidad | null) ?? 'comoda',
  );
  private readonly prefiereOscuro = signal(false);

  readonly apariencia = this._apariencia.asReadonly();
  readonly densidad = this._densidad.asReadonly();

  readonly esOscuro = computed(() => {
    const elegida = this._apariencia();
    return elegida === 'oscuro' || (elegida === 'sistema' && this.prefiereOscuro());
  });

  /** El primario del cliente, legible sobre el fondo del modo actual. */
  /** El mismo color, legible sobre el menú lateral (oscuro en los dos temas). */
  readonly colorClienteMenu = computed(() =>
    colorClienteLegible(this.sesion.clientePropio()?.config?.tema?.primaryColor, FONDO_MENU),
  );

  readonly colorCliente = computed(() =>
    colorClienteLegible(
      this.sesion.clientePropio()?.config?.tema?.primaryColor,
      this.esOscuro() ? FONDO_OSCURO : FONDO_CLARO,
    ),
  );

  constructor() {
    const media = this.documento.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
    if (media) {
      this.prefiereOscuro.set(media.matches);
      media.addEventListener('change', (e) => this.prefiereOscuro.set(e.matches));
    }

    effect(() => {
      const body = this.documento.body;
      body.classList.toggle('dark-theme', this.esOscuro());
      body.classList.toggle('densidad-compacta', this._densidad() === 'compacta');
    });

    effect(() => {
      const color = this.colorCliente();
      const estilo = this.documento.body.style;
      if (color) estilo.setProperty('--color-cliente', color);
      else estilo.removeProperty('--color-cliente');
      const menu = this.colorClienteMenu();
      if (menu) estilo.setProperty('--color-cliente-menu', menu);
      else estilo.removeProperty('--color-cliente-menu');
    });
  }

  setApariencia(apariencia: Apariencia): void {
    this._apariencia.set(apariencia);
    escribirLocal(CLAVE_APARIENCIA, apariencia);
  }

  alternarApariencia(): void {
    this.setApariencia(this.esOscuro() ? 'claro' : 'oscuro');
  }

  setDensidad(densidad: Densidad): void {
    this._densidad.set(densidad);
    escribirLocal(CLAVE_DENSIDAD, densidad);
  }
}
